import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "../../../common/u1/cn";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import { CountrySelect } from "../../../common/u1/CountrySelect";
import { getInitials } from "../../../common/u1/CompanyMark";
import { getCountryByCode } from "../../../../utils/Helper";
import {
  SearchIcon,
  BuildingIcon,
  CloseIcon,
  PlusIcon,
} from "../../../../assets/ui/icons";
import { useOwnershipSearch } from "./useOwnershipSearch";
import {
  EMPTY_STATE_TEXT,
  emptyStateFor,
  resultCountryCode,
  resultName,
  searchWarning,
} from "./ownershipSearch";

/* Pick a business from inside the dialog, without leaving it. */
export const CompanySearchField = ({
  label = "Company",
  id = "u1-company-search",
  value,
  onSelect,
  disabled = false,
  required = true,
  /* The design marks the optional field rather than the required ones on the
     insurance dialog, so a field can be required without an asterisk. */
  ariaRequired = false,
  error,
  /* The design gives each use its own chip and prompts — "COMPANY SEARCH" for
     a related entity, "INSURER SEARCH" and "BROKER SEARCH" on the insurance
     dialog — over the same field. */
  /* The field's name for aria purposes. Needed when `label` is a node — the
     insurance dialog's "Broker (optional)" pairs plain text with a muted
     span, and a node cannot go into an aria-label. */
  fieldName,
  chipLabel = "Company search",
  placeholder = "Search company…",
  manualPlaceholder = "Enter the business name",
  notFoundPrompt = "Can't find the company you're looking for?",
  addLabel = "Add company",
  className = "",
}) => {
  /* `label` may be a node ("Broker (optional)" with the second half muted), so
     it cannot go straight into an aria-label. */
  const name = fieldName || (typeof label === "string" ? label : "company");
  /* No seed: the country defaults to DEFAULT_SEARCH_COUNTRY (UAE) inside the
     hook, for every search in this flow. */
  const {
    term,
    setTerm,
    country,
    setCountry,
    results,
    isLoading,
    error: searchError,
    message,
  } = useOwnershipSearch();

  const [open, setOpen] = useState(false);
  /* Free-text mode, reached from "Add company". */
  const [manual, setManual] = useState(false);
  const ref = useRef(null);
  const fieldRef = useRef(null);
  const menuRef = useRef(null);
  const [rect, setRect] = useState(null);

  /* A dropdown left open behind a click elsewhere would sit over the form.
     The menu is portalled out of `ref`, so it needs checking separately or a
     click on a result would close the menu before the click landed. */
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      const inField = ref.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      /* THE COUNTRY-PICKER BUG. The country select inside this menu portals its own dropdown to document.body, so a click on a country row is inside NEITHER `ref`... */
      const inCountryMenu = e.target?.closest?.(".u1-country-menu");
      if (!inField && !inMenu && !inCountryMenu) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  /* Measured before paint so the menu never flashes at the wrong place. */
  useLayoutEffect(() => {
    if (!open || manual) return undefined;

    const measure = () => {
      const box = fieldRef.current?.getBoundingClientRect();
      if (box) setRect({ top: box.bottom + 6, left: box.left, width: box.width });
    };
    measure();

    /* A fixed menu does not travel with a scrolling container, so rather than drift away from its field it closes. */
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      /* Same reason as the click handler: scrolling the nested country list is
         not scrolling away from this menu. */
      if (e.target?.closest?.(".u1-country-menu")) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open, manual]);

  const emptyState = emptyStateFor({ term, results, isLoading, error: searchError });
  const warning = searchWarning(message);
  /* emptyStateFor reports nothing mid-typing, which suits the full-page step's
     illustration but leaves an inline dropdown looking broken. */
  const belowMinimum = term.length > 0 && term.trim().length < 3;

  const choose = (company) => {
    onSelect?.(company);
    setOpen(false);
    setTerm("");
  };

  /* A chosen business is the field's value, with a way to change it rather
     than a dead read-only box. */
  if (value && !manual) {
    return (
      <div className={className}>
        <FieldLabel as="span" variant="field" required={required}>
          {label}
        </FieldLabel>
        <div
          className={cn(
            "flex h-11 w-full items-center gap-2.5 rounded-lg border px-3.5",
            "border-[var(--field-border)] bg-[var(--field-inset)]"
          )}
        >
          <span aria-hidden="true" className="shrink-0 text-[var(--muted-2)]">
            <BuildingIcon size={15} />
          </span>
          <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-[var(--ink)] font-sans">
            {value}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={() => onSelect?.(null)}
              aria-label={`Clear ${name}`}
              className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                "text-[var(--muted)] transition-colors hover:bg-[var(--line)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
              )}
            >
              <CloseIcon size={12} />
            </button>
          )}
        </div>
      </div>
    );
  }

  /* Transcribed from the result payload: `industry.sub_industry` is a list, and
     the design shows one. */
  const industryOf = (company) => {
    const list = company?.industry?.sub_industry;
    const first = Array.isArray(list) ? list[0] : list;
    return typeof first === "string" ? first : first?.sub_industry || "";
  };

  return (
    <div className={cn("relative", className)} ref={ref}>
      <FieldLabel htmlFor={id} variant="field" required={required}>
        {label}
      </FieldLabel>

      <div className="relative" ref={fieldRef}>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted-2)]"
        >
          <SearchIcon size={14} />
        </span>
        <input
          id={id}
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-results`}
          aria-autocomplete="list"
          aria-required={required || ariaRequired ? "true" : undefined}
          autoComplete="off"
          disabled={disabled}
          value={term}
          placeholder={manual ? manualPlaceholder : placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setTerm(e.target.value);
            if (manual) {
              /* A typed name IS the chosen business, with no id behind it. */
              onSelect?.(e.target.value ? { name: e.target.value } : null);
              return;
            }
            setOpen(true);
          }}
          className={cn(
            "h-11 w-full rounded-lg border bg-white pl-10 text-[14px]",
            manual ? "pr-3.5" : "pr-[128px]",
            "text-[var(--ink)] font-sans transition-[border-color,box-shadow] duration-150",
            "focus:outline-none focus:border-[var(--orange)] focus:shadow-[0_0_0_4px_var(--focus-ring)]",
            "disabled:cursor-not-allowed disabled:bg-[var(--field-inset)]",
            error
              ? "border-[var(--coral)]"
              : "border-[var(--field-border)] hover:border-[var(--field-border-hover)]"
          )}
        />

        {/* The design's chip at the field's trailing edge. Decorative and
            pointer-transparent, so it never blocks a click into the input. */}
        {!manual && (
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2",
              "rounded-md bg-[var(--coral-soft)] px-2.5 py-1.5",
              "text-[10px] font-bold uppercase tracking-[0.06em]",
              "text-[var(--coral-deep)] font-sans"
            )}
          >
            {chipLabel}
          </span>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans">
          {error}
        </p>
      )}

      {open &&
        !manual &&
        createPortal(
          /* Wrapped in u1-root: every design token this menu paints with is
             scoped to that class, and the portal lands outside it. */
          <div className="u1-root">
            <div
              ref={menuRef}
              /* No role and no text of its own, so a test id is the only way to
                 reach it — the alternative is walking up from a child. */
              data-testid="company-search-menu"
              className={cn(
                "overflow-hidden rounded-[12px]",
                "border border-[var(--line)] bg-white shadow-[shadow:var(--menu-shadow)]"
              )}
              /* Measured, so it cannot be a Tailwind class. `fixed` is what
                 escapes the dialog's scrolling body. */
              style={
                rect
                  ? {
                      position: "fixed",
                      /* The country picker opens from inside this menu and
                         must paint over it — see the --z-menu scale. */
                      zIndex: "var(--z-menu)",
                      top: rect.top,
                      left: rect.left,
                      width: rect.width,
                    }
                  : { position: "fixed", zIndex: "var(--z-menu)", visibility: "hidden" }
              }
            >
          {/* The search cannot run without a country — see the note above. */}
          <div className="flex items-center gap-2 border-b border-[var(--line)] px-2.5 py-2">
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--muted)] font-sans">
              In
            </span>
            <CountrySelect
              variant="bar"
              size="sm"
              /* Not "Search country": CountrySelect's own filter input is
                 labelled that, and two identical names in one dropdown are
                 indistinguishable to a screen reader. */
              ariaLabel="Country to search"
              value={country}
              onChange={(c) => setCountry(c?.code)}
              placeholder="Select a country"
              nameClassName=""
            />
          </div>

          {warning && (
            <p
              role="status"
              className="m-0 border-b border-[var(--line)] bg-[var(--gold-soft)] px-3 py-2 text-[11.5px] text-[var(--gold-deep)] font-sans"
            >
              {warning}
            </p>
          )}

          {isLoading && (
            <p
              role="status"
              aria-live="polite"
              className="m-0 px-3 py-3 text-[12.5px] text-[var(--muted)] font-sans"
            >
              Searching…
            </p>
          )}

          {!isLoading && belowMinimum && (
            <p className="m-0 px-3 py-3 text-[12.5px] text-[var(--muted)] font-sans">
              Type at least 3 characters to search.
            </p>
          )}

          {!isLoading && !belowMinimum && emptyState && (
            <p className="m-0 px-3 py-3 text-[12.5px] text-[var(--muted)] font-sans">
              {emptyState === "invite"
                ? "Type at least 3 characters to search."
                : EMPTY_STATE_TEXT[emptyState]}
            </p>
          )}

          {results.length > 0 && (
            <ul
              id={`${id}-results`}
              aria-label={`${name} results`}
              className="m-0 flex max-h-[236px] list-none flex-col overflow-y-auto p-0"
            >
              {results.map((company, index) => {
                const name = resultName(company);
                const industry = industryOf(company);
                const place = getCountryByCode(resultCountryCode(company));
                return (
                  <li key={`${name}-${index}`}>
                    <button
                      type="button"
                      onClick={() => choose(company)}
                      className={cn(
                        "flex w-full items-center gap-3 border-b border-[var(--line)] px-3 py-2.5 text-left",
                        "transition-colors last:border-b-0 hover:bg-[var(--bg)]",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
                        "focus-visible:ring-[var(--coral)]"
                      )}
                    >
                      {/* Initials, from the same helper the company marks use
                          across the app. */}
                      <span
                        aria-hidden="true"
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                          "bg-[var(--coral-soft)] text-[11px] font-bold",
                          "text-[var(--coral-deep)] font-sans"
                        )}
                      >
                        {getInitials(name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-bold text-[var(--ink)] font-sans">
                          {name}
                        </span>
                        <span className="block truncate text-[11.5px] text-[var(--muted)] font-sans">
                          {[place, industry].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--line)] px-3 py-2.5">
            <p className="m-0 text-[12px] text-[var(--muted)] font-sans">
              {notFoundPrompt}
            </p>
            <button
              type="button"
              onClick={() => {
                setManual(true);
                setOpen(false);
                setTerm("");
              }}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-1.5 py-1",
                "text-[12px] font-bold text-[var(--coral)] font-sans",
                "transition-colors hover:text-[var(--coral-deep)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
              )}
            >
              <PlusIcon size={11} />
              {addLabel}
            </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CompanySearchField;
